// The Meta v4 flow is selected by config_id in Facebook Login for Business.
// Never put an app secret or an access token in this module.
const META_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com']);
let sdkPromise;

export function parseSignupEvent(event) {
  if (!META_ORIGINS.has(event.origin)) return null;
  let data = event.data;
  try { if (typeof data === 'string') data = JSON.parse(data); } catch { return null; }
  if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return null;
  return data;
}

export function loadMetaSdk(appId, graphVersion) {
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      if (window.FB) return resolve(window.FB);
      const script = document.createElement('script');
      // Meta's popup follows the SDK locale; ?lang=en shows it in English for reviewers.
      script.src = `https://connect.facebook.net/${localStorage.getItem('uiLang') === 'en' ? 'en_US' : 'es_LA'}/sdk.js`;
      script.async = true;
      const timer = setTimeout(() => { script.remove(); reject(new Error('No se pudo cargar Meta. Intenta de nuevo.')); }, 15000);
      script.onload = () => { clearTimeout(timer); window.FB ? resolve(window.FB) : reject(new Error('Meta no está disponible.')); };
      script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('No se pudo cargar Meta. Revisa tu conexión.')); };
      document.head.appendChild(script);
    }).catch(error => { sdkPromise = undefined; throw error; });
  }
  return sdkPromise.then(fb => {
    fb.init({ appId, version: graphVersion, xfbml: false, cookie: false });
    return fb;
  });
}

// Session events that end with a connectable number (see CONNECT_MODES in the backend).
const CONNECT_EVENTS = new Set(['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING']);

// onReport receives sessions that ended without a connectable number (cancel,
// error, partial) so the backend can record where the clinic stopped.
export function launchMetaSignup(fb, configId, extras = { setup: {} }, onReport = () => {}) {
  let cancel;
  const promise = new Promise((resolve, reject) => {
    let code;
    let assets;
    let settled = false;
    const cleanup = () => { clearTimeout(timer); window.removeEventListener('message', onMessage); };
    const fail = message => { if (settled) return; settled = true; cleanup(); reject(new Error(message)); };
    const finish = () => {
      if (settled || !code || !assets) return;
      settled = true; cleanup(); resolve({ code, ...assets });
    };
    const onMessage = event => {
      const info = parseSignupEvent(event);
      if (!info) return;
      if (info.event === 'CANCEL' || info.event === 'ERROR') {
        onReport(info);
        return fail('No se completó la autorización en Meta. Puedes volver a intentarlo.');
      }
      if (!CONNECT_EVENTS.has(info.event)) {
        if (String(info.event).startsWith('FINISH')) {
          onReport(info);
          fail('La conexión quedó incompleta: falta elegir o verificar un número. Vuelve a intentarlo.');
        }
        return;
      }
      const { waba_id, phone_number_id } = info.data || {};
      const coex = info.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';
      if (!/^\d{5,30}$/.test(waba_id || '') || ((!coex || phone_number_id) && !/^\d{5,30}$/.test(phone_number_id || ''))) return fail('Meta no devolvió una cuenta y un número válidos.');
      // Coexistence can return only the WABA. The server resolves a unique,
      // authorized Business App number; the browser never guesses one.
      assets = { waba_id, ...(phone_number_id ? { phone_number_id } : {}), event: info.event }; finish();
    };
    const timer = setTimeout(() => fail('La sesión de Meta expiró. Vuelve a conectar.'), 9 * 60 * 1000);
    cancel = () => fail('Conexión cancelada.');
    window.addEventListener('message', onMessage);
    try {
      fb.login(response => {
        if (!response.authResponse?.code) return fail('No se autorizó la conexión con Meta.');
        code = response.authResponse.code; finish();
      }, { config_id: configId, response_type: 'code', override_default_response_type: true, extras });
    } catch { fail('No se pudo abrir Meta. Permite la ventana emergente y vuelve a intentarlo.'); }
  });
  return { promise, cancel: () => cancel?.() };
}
